import { FC, ReactNode } from 'react';

import {
  CloudDownloadIcon,
  DownloadFileIcon,
  EditDocumentIcon,
  EyeIconSmall,
  SendReminderIcon,
} from '../../../../../../assets/icons/index';
import { Actions } from '../../../../../../components/actions/index';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import Button from '../../../../../../components/button/index';
import { Tag, TagVariant } from '../../../../../../components/tag/index';
import { useFormat, useLocale } from '../../../../../../hooks/index';
import { selectedCompanyStorage } from '../../../../../../storage/company/selected-company.util';
import { getSignatureCategoryDisplayLabel } from '../../../../constants/document-category.constant';
import { SignaturePopupType } from '../../../enums/signature-popup-type.const';
import {
  SignatureStatus,
  signatureStatusTranslationKey,
} from '../../../enums/signature-status.const';
import { useCanSendSignatureReminder } from '../../../hooks/use-can-send-signature-reminder.hook';
import { useDownloadSignatureDocument } from '../../../hooks/use-download-signature-document.hook';
import { SignaturePopupStore } from '../../../store/signature-popup.store';
import { SignatureRow } from '../../../types/signature-row.types';
import { toViewDocumentDetailsMetadata } from '../../../utils/to-view-document-details-metadata.util';
import { Column, columnTitleMap } from '../signature-table.types';

const MOBILE_DETAIL_COLUMNS_ALL: Column[] = [
  Column.CATEGORY,
  Column.SIGNATORIES,
  Column.LAST_SENT,
  Column.EXPIRY,
];

const MOBILE_DETAIL_COLUMNS_MY_SIGNATURES: Column[] = [Column.CATEGORY, Column.EXPIRY];

const getStatusVariant = (status: SignatureStatus): TagVariant => {
  switch (status) {
    case SignatureStatus.DOCUMENT_EXPIRED:
      return TagVariant.RED;
    case SignatureStatus.FULLY_SIGNED:
      return TagVariant.GREEN;
    case SignatureStatus.PARTIALLY_SIGNED:
      return TagVariant.BLUE;
    case SignatureStatus.SIGNATURE_PENDING:
      return TagVariant.YELLOW;
    case SignatureStatus.VOIDED:
      return TagVariant.GRAY;
    default:
      return TagVariant.GRAY;
  }
};

type DetailRowProps = {
  label: string;
  value: ReactNode;
};

const DetailRow: FC<DetailRowProps> = ({ label, value }) => (
  <div className="flex justify-between gap-3 px-3 py-4">
    <span className="shrink-0 text-sm text-gray-700">{label}</span>
    <span className="min-w-0 text-right text-sm text-gray-700">{value}</span>
  </div>
);

const SignatoriesPreview: FC<{
  signatories: SignatureRow['signatories'];
  totalCount: number;
}> = ({ signatories, totalCount }) => {
  const first = signatories?.[0];
  const restCount = totalCount - 1;

  if (!first) return <span className="text-sm text-gray-700">-</span>;

  const primaryName = first.stakeholder?.fullName ?? '-';

  return (
    <span className="text-sm text-gray-700">
      {primaryName}
      {restCount > 0 && <span className="ml-1 text-brand-700">+{restCount}</span>}
    </span>
  );
};

export type SignatureMobileCardProps = {
  data: SignatureRow;
  isMySignaturesScope?: boolean;
};

export const SignatureMobileCard: FC<SignatureMobileCardProps> = ({
  data,
  isMySignaturesScope,
}) => {
  const { format } = useFormat();
  const { messagesLocale } = useLocale();
  const openPopup = SignaturePopupStore(({ openPopup }) => openPopup);
  const { download } = useDownloadSignatureDocument();

  const { name, category, status, signatories, lastSentAt, expiresAt, id } = data;

  const totalCount = signatories?.length ?? 0;
  const signedCount = signatories?.filter((s) => s.isSigned).length ?? 0;

  const categoryLabel = getSignatureCategoryDisplayLabel(category, messagesLocale);
  const lastSentLabel = lastSentAt ? format(new Date(lastSentAt), 'dd MMM, yyyy') : '-';
  const expiryLabel = expiresAt ? format(new Date(expiresAt), 'dd MMM yyyy') : '-';

  const currentUserStakeholderId = selectedCompanyStorage.getState().company?.stakeholder?.id;
  const currentUserSignatory = currentUserStakeholderId
    ? signatories?.find((s) => s.stakeholderId === currentUserStakeholderId)
    : signatories?.[0];
  const isCurrentUserSigned = currentUserSignatory?.isSigned ?? false;
  const isExpired = status === SignatureStatus.DOCUMENT_EXPIRED;
  const isVoided = status === SignatureStatus.VOIDED;
  const usePersonalizedMySignaturesStatus = isMySignaturesScope && !isVoided;

  const getMySignaturesStatus = (): { label: string; variant: TagVariant } => {
    if (isExpired) {
      return { label: messagesLocale['EXPIRED'], variant: TagVariant.RED };
    }
    if (isCurrentUserSigned) {
      return { label: messagesLocale['SIGNED'], variant: TagVariant.GREEN };
    }
    return {
      label: messagesLocale['SIGNATURE_STATUS_SIGNATURE_PENDING'],
      variant: TagVariant.YELLOW,
    };
  };

  const baseStatusLabel =
    status in signatureStatusTranslationKey
      ? messagesLocale[signatureStatusTranslationKey[status as SignatureStatus]]
      : status;
  const mySignaturesStatus = usePersonalizedMySignaturesStatus ? getMySignaturesStatus() : null;
  const statusLabel = mySignaturesStatus
    ? mySignaturesStatus.label
    : status === SignatureStatus.PARTIALLY_SIGNED && totalCount > 0
      ? `${baseStatusLabel} ${signedCount}/${totalCount}`
      : baseStatusLabel;

  const statusVariant = mySignaturesStatus ? mySignaturesStatus.variant : getStatusVariant(status);

  const isPendingOrPartial =
    status === SignatureStatus.SIGNATURE_PENDING || status === SignatureStatus.PARTIALLY_SIGNED;
  const cardBgClass = isMySignaturesScope && isPendingOrPartial ? 'bg-yellow-50' : 'bg-white';

  const detailColumns = isMySignaturesScope
    ? MOBILE_DETAIL_COLUMNS_MY_SIGNATURES
    : MOBILE_DETAIL_COLUMNS_ALL;

  const reviewButtonDisabled = isMySignaturesScope && isExpired;
  const signButtonDisabled = isMySignaturesScope && (isExpired || isVoided);
  const showMySignaturesFooter = isMySignaturesScope && !isCurrentUserSigned;

  const canSendReminder = useCanSendSignatureReminder(status);
  const showSendReminder = !isMySignaturesScope && canSendReminder;

  const viewDetailsAction = {
    title: messagesLocale['VIEW_DETAILS'],
    icon: <EyeIconSmall />,
    callback: () =>
      openPopup({
        metadata: toViewDocumentDetailsMetadata(data),
        type: SignaturePopupType.VIEW_DOCUMENT_DETAILS,
      }),
  };

  return (
    <>
      <div
        className={`flex w-full min-w-[300px] max-w-[440px] flex-col rounded-lg border border-gray-200 ${cardBgClass}`}
      >
        <div className="px-4 pt-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-base font-semibold text-gray-700">{name}</h3>
              <Tag className="mt-1 w-fit" title={statusLabel} variant={statusVariant} />
            </div>
            <Actions
              actions={[
                viewDetailsAction,
                ...(showSendReminder
                  ? [
                      {
                        title: messagesLocale['SEND_REMINDER'],
                        icon: <SendReminderIcon className="size-4" iconColor="#344054" />,
                        callback: () =>
                          openPopup({
                            metadata: { documentName: name, signatureId: id },
                            type: SignaturePopupType.SEND_REMINDER,
                          }),
                      },
                    ]
                  : []),
                {
                  title: isMySignaturesScope
                    ? messagesLocale['REVIEW']
                    : messagesLocale['VIEW_DOCUMENT'],
                  icon: <DownloadFileIcon iconColor="#344054" />,
                  disabled: reviewButtonDisabled,
                  callback: () =>
                    openPopup({
                      metadata: { documentName: name, signatureId: id },
                      type: SignaturePopupType.VIEW_DOCUMENT,
                    }),
                },
                {
                  title: messagesLocale['DOWNLOAD'],
                  icon: <CloudDownloadIcon iconColor="#344054" />,
                  callback: () => download(id),
                },
              ]}
            />
          </div>
        </div>

        <div className="mt-4 divide-y-[1px] border-t border-gray-100">
          {detailColumns.map((column) => {
            const label = messagesLocale[columnTitleMap[column]];
            let value: ReactNode;

            switch (column) {
              case Column.CATEGORY:
                value = <span className="text-sm text-gray-700">{categoryLabel}</span>;
                break;
              case Column.EXPIRY:
                value = <span className="text-sm text-gray-700">{expiryLabel}</span>;
                break;
              case Column.LAST_SENT:
                value = <span className="text-sm text-gray-700">{lastSentLabel}</span>;
                break;
              case Column.SIGNATORIES:
                value = <SignatoriesPreview signatories={signatories} totalCount={totalCount} />;
                break;
              default:
                value = '-';
            }

            return <DetailRow key={column} label={label} value={value} />;
          })}
        </div>

        {showMySignaturesFooter ? (
          <div className="flex justify-end gap-2 border-t border-gray-100 px-3 py-4">
            {!isCurrentUserSigned && (
              <Button
                className="min-w-0 flex-1 gap-1.5 px-3 py-1.5 font-normal"
                disabled={signButtonDisabled}
                onClick={() =>
                  openPopup({
                    metadata: { documentName: name, signatureId: id },
                    type: SignaturePopupType.SIGN_DOCUMENT,
                  })
                }
                type="button"
              >
                <EditDocumentIcon
                  className="size-[18px]"
                  iconColor={isExpired || isVoided ? '#d1d6de' : '#fff'}
                />
                {messagesLocale['SIGN_DOCUMENT']}
              </Button>
            )}
          </div>
        ) : !isMySignaturesScope ? (
          <button
            className="h-full w-full rounded-b-lg border-t border-gray-100 bg-brand-25 px-3 py-3 text-start text-sm text-brand-700"
            onClick={() =>
              openPopup({
                metadata: { documentName: name, signatories: signatories ?? [] },
                type: SignaturePopupType.VIEW_SIGNATURES,
              })
            }
            type="button"
          >
            <AppFormattedMessage id="VIEW_ALL_SIGNATURES" values={{ count: totalCount }} />
          </button>
        ) : null}
      </div>
    </>
  );
};
